import * as React from 'react';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/react';
import type {
  Middleware,
  Placement,
  UseFloatingOptions,
} from '@floating-ui/react';
import { PluginKey } from '@tiptap/pm/state';

// --- Hooks ---
import { useFloatingElement } from '../../../../app/rte/hooks/use-floating-element';
import { useMenuNavigation } from '../../../../app/rte/hooks/use-menu-navigation';
import { useTiptapEditor } from '../../../../app/rte/hooks/use-tiptap-editor';

// --- Tiptap UI ---
import { Suggestion } from '@tiptap/suggestion';

// --- UI Primitives ---
import {
  SuggestionPluginKey,
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';

import type {
  SuggestionItem,
  SuggestionMenuProps,
} from './suggestion-menu-types';

/**
 * Gap kept between the suggestion menu and the viewport edges when
 * collision-positioning, so a tall menu never sits flush against the screen edge.
 */
const SUGGESTION_MENU_VIEWPORT_PADDING_PX = 16;

/**
 * A component that renders a suggestion menu for Tiptap editors.
 * Displays a floating menu when a trigger character is typed.
 */
export const SuggestionMenu = ({
  editor: providedEditor,
  floatingOptions,
  selector = 'tiptap-suggestion-menu',
  children,
  maxHeight = 384,
  pluginKey = SuggestionPluginKey,
  itemsOverrideFn,
  beforeKeyDown,
  anchorToEditorStart = false,
  deleteTriggerOnSelect = true,
  portal = false,
  ...internalSuggestionProps
}: SuggestionMenuProps) => {
  const { editor } = useTiptapEditor(providedEditor);

  const [show, setShow] = React.useState<boolean>(false);
  const [internalClientRect, setInternalClientRect] =
    React.useState<DOMRect | null>(null);
  const lockedPlacementRef = React.useRef<Placement | null>(null);

  const resetLockedPlacement = React.useCallback(() => {
    lockedPlacementRef.current = null;
  }, []);

  const updateInternalClientRect = React.useCallback(
    (clientRect: DOMRect | null) => {
      if (clientRect === null) {
        setInternalClientRect(null);
        return;
      }

      setInternalClientRect((current) => {
        if (current === null || lockedPlacementRef.current === null) {
          return clientRect;
        }

        return new DOMRect(
          clientRect.left,
          current.top,
          clientRect.width,
          clientRect.height
        );
      });
    },
    []
  );

  const whileElementsMounted = React.useCallback<
    NonNullable<UseFloatingOptions['whileElementsMounted']>
  >((reference, floating, update) => {
    return autoUpdate(reference, floating, update, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: false,
    });
  }, []);

  const handlePlacement = React.useCallback((placement: Placement) => {
    lockedPlacementRef.current = placement;
  }, []);

  const placementLockMiddleware = React.useMemo<Middleware>(
    () => ({
      name: 'placementLock',
      fn({ placement }) {
        handlePlacement(placement);
        return {};
      },
    }),
    [handlePlacement]
  );

  // When anchoring to the editor start, position the menu at the editor's left
  // edge (keeping the caret's vertical line) instead of at the trigger char, so
  // a wide multi-column menu opens at the start of the input and flows rightward.
  const referenceRect = React.useMemo<DOMRect | null>(() => {
    if (
      !anchorToEditorStart ||
      internalClientRect === null ||
      editor == null ||
      editor.isDestroyed
    ) {
      return internalClientRect;
    }
    const editorRect = editor.view.dom.getBoundingClientRect();
    return new DOMRect(
      editorRect.left,
      internalClientRect.top,
      editorRect.width,
      internalClientRect.height
    );
  }, [anchorToEditorStart, internalClientRect, editor]);
  const [internalCommand, setInternalCommand] = React.useState<
    ((item: SuggestionItem) => void) | null
  >(null);
  const [internalItems, setInternalItems] = React.useState<SuggestionItem[]>(
    []
  );
  const [internalQuery, setInternalQuery] = React.useState<string>('');

  const { ref, style, getFloatingProps, isMounted } = useFloatingElement(
    show,
    referenceRect,
    1000,
    {
      // Reposition when the menu itself resizes, not just when the caret moves.
      // Once Floating UI chooses above or below, keep that side stable while the
      // user keeps typing. We still listen to scroll/resize so the next update can
      // choose a new side if the viewport changes.
      whileElementsMounted,
      strategy: 'fixed',
      placement: lockedPlacementRef.current ?? 'bottom-start',
      middleware: [
        offset(10),
        flip({
          mainAxis: lockedPlacementRef.current === null,
          crossAxis: false,
        }),
        // Keep the menu off the viewport edges: `shift` slides it in and `size`
        // (via the same padding) shrinks its available height, so a tall menu never
        // sits flush against the bottom/edge — it always leaves a little breathing
        // room and scrolls the overflow instead.
        shift({ padding: SUGGESTION_MENU_VIEWPORT_PADDING_PX }),
        size({
          padding: SUGGESTION_MENU_VIEWPORT_PADDING_PX,
          apply({ availableHeight, elements }) {
            if (elements.floating != null) {
              const maxHeightValue =
                maxHeight != null
                  ? Math.min(maxHeight, availableHeight)
                  : availableHeight;

              elements.floating.style.setProperty(
                '--suggestion-menu-max-height',
                `${maxHeightValue}px`
              );
            }
          },
        }),
        placementLockMiddleware,
      ],
      onOpenChange(open) {
        if (!open) {
          resetLockedPlacement();
          setShow(false);
        }
      },
      ...floatingOptions,
    }
  );

  const internalSuggestionPropsRef = React.useRef(internalSuggestionProps);
  const deleteTriggerOnSelectRef = React.useRef(deleteTriggerOnSelect);

  React.useEffect(() => {
    internalSuggestionPropsRef.current = internalSuggestionProps;
  }, [internalSuggestionProps]);

  React.useEffect(() => {
    deleteTriggerOnSelectRef.current = deleteTriggerOnSelect;
  }, [deleteTriggerOnSelect]);

  const closePopup = React.useCallback(() => {
    resetLockedPlacement();
    setShow(false);
  }, [resetLockedPlacement]);

  // The exact PluginKey instance the Suggestion plugin was registered with, so we
  // can read its state (the active trigger range) to end the session on demand.
  const suggestionPluginKeyRef = React.useRef<PluginKey | null>(null);

  // Fully end the @ suggestion session (not just hide the popup): delete the
  // in-progress trigger text so the plugin exits and its decoration is removed.
  // Used when navigating away (e.g. opening a tool's details modal).
  const endSuggestionSession = React.useCallback(() => {
    closePopup();
    const key = suggestionPluginKeyRef.current;
    if (editor == null || editor.isDestroyed || key == null) {
      return;
    }
    const state = key.getState(editor.state) as
      | { active?: boolean; range?: { from: number; to: number } }
      | undefined;
    if (state?.active === true && state.range != null) {
      editor.commands.deleteRange(state.range);
    }
  }, [editor, closePopup]);

  // Create a stable wrapper that always reads items through the ref
  const itemsWrapper = React.useCallback(
    async (props: { query: string; editor: any; signal: AbortSignal }) => {
      if (internalSuggestionPropsRef.current.items) {
        return internalSuggestionPropsRef.current.items(props);
      }
      return [];
    },
    []
  );

  React.useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    const existingPlugin = editor.state.plugins.find(
      (plugin) => plugin.spec.key === pluginKey
    );
    if (existingPlugin) {
      editor.unregisterPlugin(pluginKey);
    }

    const resolvedPluginKey =
      pluginKey instanceof PluginKey ? pluginKey : new PluginKey(pluginKey);
    suggestionPluginKeyRef.current = resolvedPluginKey;

    const suggestion = Suggestion({
      pluginKey: resolvedPluginKey,
      editor,

      command({ editor, range, props }) {
        if (range == null) {
          return;
        }

        const { view, state } = editor;

        const isMention = editor.extensionManager.extensions.some(
          (extension) => {
            const name = extension.name;
            return (
              name === 'mention' &&
              extension.options?.suggestion?.char ===
                internalSuggestionPropsRef.current.char
            );
          }
        );

        // Mentions delete+insert themselves. Other menus either delete here
        // (default) or opt out so `onSelect` can do an atomic replace.
        if (
          !isMention &&
          deleteTriggerOnSelectRef.current &&
          range.from < range.to
        ) {
          view.dispatch(state.tr.deleteRange(range.from, range.to));
        }

        const nodeAfter = view.state.selection.$to.nodeAfter;
        const overrideSpace = nodeAfter?.text?.startsWith(' ');

        const rangeToUse = { ...range };

        if (overrideSpace) {
          rangeToUse.to += 1;
        }

        props.onSelect({ editor, range: rangeToUse, context: props.context });
      },

      render: () => {
        return {
          onStart: (props: SuggestionProps<SuggestionItem>) => {
            setInternalCommand(() => props.command);
            setInternalItems(props.items);
            setInternalQuery(props.query);
            updateInternalClientRect(props.clientRect?.() ?? null);
            setShow(true);
          },

          onUpdate: (props: SuggestionProps<SuggestionItem>) => {
            setInternalCommand(() => props.command);
            setInternalItems(props.items);
            setInternalQuery(props.query);
            updateInternalClientRect(props.clientRect?.() ?? null);
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === 'Escape') {
              closePopup();
              return true;
            }
            return false;
          },

          onExit: () => {
            resetLockedPlacement();
            setInternalCommand(null);
            setInternalItems([]);
            setInternalQuery('');
            setInternalClientRect(null);
            setShow(false);
          },
        };
      },
      ...internalSuggestionPropsRef.current,
      items: itemsWrapper,
    });

    editor.registerPlugin(suggestion);

    return () => {
      if (!editor.isDestroyed) {
        editor.unregisterPlugin(pluginKey);
      }
    };
  }, [
    editor,
    pluginKey,
    closePopup,
    itemsWrapper,
    resetLockedPlacement,
    updateInternalClientRect,
  ]);

  const onSelect = React.useCallback(
    (item: SuggestionItem) => {
      closePopup();

      if (internalCommand) {
        internalCommand(item);
      }
    },
    [closePopup, internalCommand]
  );

  const displayItems =
    itemsOverrideFn !== undefined
      ? itemsOverrideFn(internalQuery)
      : internalItems;

  // Only enable keyboard navigation when the menu is actually visible.
  // useMenuNavigation registers a capture-phase keydown listener that
  // intercepts Enter (and arrow keys, etc.). If we pass items when the
  // menu is hidden, the listener swallows Enter and prevents normal
  // editor behaviour (e.g. inserting new lines).
  const { selectedIndex } = useMenuNavigation({
    editor: editor,
    query: internalQuery,
    items: show ? displayItems : [],
    onSelect,
    beforeKeyDown: show && displayItems.length > 0 ? beforeKeyDown : undefined,
  });

  if (!isMounted || !show || !editor || displayItems.length === 0) {
    return null;
  }

  const menu = (
    <div
      ref={ref}
      style={style}
      {...getFloatingProps()}
      data-selector={selector}
      className="tiptap-suggestion-menu"
      role="listbox"
      aria-label="Suggestions"
      onPointerDown={(e) => e.preventDefault()}
    >
      {children({
        items: displayItems,
        selectedIndex,
        onSelect,
        query: internalQuery,
        editor,
        closeMenu: endSuggestionSession,
      })}
    </div>
  );

  return portal ? <FloatingPortal>{menu}</FloatingPortal> : menu;
};
